/**
 * Costing API service (DEV — costing).
 *
 * Typed client for the per-program cost-estimate grid. Uses raw fetch + the
 * JWT token (matching the inline-embeddable program-tab components) so the xlsx
 * export can stream a blob.
 */
import { tokenManager } from '../lib/api'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export interface UnitOfMeasure {
  id: number
  code: string
  name: string
  is_lump_sum: boolean
}

export interface CostCell {
  id: number | null
  year: number
  quantity: string
  effective_unit_cost: string
  total: string
}

/** Color of money (appropriation). Empty string = untagged. */
export type Appropriation = 'rdte' | 'procurement' | 'os' | ''

/** Display labels + canonical tab order for the colors of money. */
export const APPROPRIATION_LABELS: Record<string, string> = {
  rdte: 'RDT&E',
  procurement: 'Procurement',
  os: 'O&S',
  '': 'Other',
}
export const APPROPRIATION_ORDER: Appropriation[] = ['rdte', 'procurement', 'os', '']

export interface CostLineItemRow {
  id: number
  cost_model: number
  category: number
  name: string
  unit_of_measure: UnitOfMeasure
  unit_cost: string
  cost_behavior: 'one_time' | 'scales_with_quantity' | 'recurs_annually'
  appropriation: Appropriation
  estimate_source: 'comps' | 'research' | 'blind' | 'template' | 'user'
  order: number
  is_active: boolean
  years: CostCell[]
}

export interface CostCategoryNode {
  id: number
  cost_model: number
  psc: number | null
  psc_code: string | null
  appropriation: Appropriation
  parent: number | null
  name: string
  order: number
  is_active: boolean
  children: CostCategoryNode[]
  line_items: CostLineItemRow[]
  year_subtotals: Record<string, string>
}

/** First-class acquisition figures (color subtotals + APUC / PAUC / system cost).
 *  apuc/pauc are null when not computable (no quantity or no procurement). */
export interface UnitCostMetrics {
  color_subtotals: Record<string, string>
  system_cost: string
  apuc: string | null
  pauc: string | null
}

export interface CostModelGrid {
  id: number
  program: number
  is_active: boolean
  program_quantity: number | null
  service_life_years: number | null
  base_year: number
  year_span: number
  inflation_rate: string
  appropriation_inflation: Record<string, number>
  years: number[]
  categories: CostCategoryNode[]
  grand_totals: Record<string, string>
  unit_cost_metrics: UnitCostMetrics
}

export interface ProductServiceCode {
  id: number
  code: string
  name: string
  description: string
  parent: number | null
  psc_level: number
  is_active: boolean
}

export interface SubtreeCounts {
  sub_categories: number
  line_items: number
}

export function authHeaders(json = true): Record<string, string> {
  const h: Record<string, string> = { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
  if (json) h['Content-Type'] = 'application/json'
  return h
}

/** Pull a human-readable message out of a DRF error body (field errors like
 *  `{quantity: ['…']}`, or `{detail}` / `{error}`), falling back to the status. */
export async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `Request failed (${res.status})`
    try {
      const body = await res.json()
      const first =
        body?.quantity ?? body?.year ?? body?.category ?? body?.detail ?? body?.error ??
        (body && typeof body === 'object' ? Object.values(body)[0] : undefined)  // any other field error
      if (first) message = Array.isArray(first) ? first.join(' ') : String(first)
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new Error(message)
  }
  return res.json() as Promise<T>
}

export const costingApi = {
  /** The program's active cost model (nested grid), or null if none exists. */
  async getByProgram(programId: string | number): Promise<CostModelGrid | null> {
    const res = await fetch(
      `${API_BASE_URL}/api/cost-models/?program=${programId}&is_active=true`,
      { headers: authHeaders(false) },
    )
    const data = await unwrap<any>(res)
    const list: CostModelGrid[] = Array.isArray(data) ? data : (data.results ?? [])
    return list.length ? list[0] : null
  },

  async getCostModel(id: number): Promise<CostModelGrid> {
    const res = await fetch(`${API_BASE_URL}/api/cost-models/${id}/`, { headers: authHeaders(false) })
    return unwrap<CostModelGrid>(res)
  },

  async patchLineItem(id: number, body: Partial<{
    name: string; unit_cost: string; category: number; cost_behavior: string; unit_of_measure_id: number
  }>): Promise<CostLineItemRow> {
    const res = await fetch(`${API_BASE_URL}/api/cost-line-items/${id}/`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body),
    })
    return unwrap<CostLineItemRow>(res)
  },

  async patchYear(id: number, quantity: string): Promise<CostCell> {
    const res = await fetch(`${API_BASE_URL}/api/cost-line-item-years/${id}/`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ quantity }),
    })
    return unwrap<CostCell>(res)
  },

  async createYear(lineItem: number, year: number, quantity: string): Promise<CostCell> {
    const res = await fetch(`${API_BASE_URL}/api/cost-line-item-years/`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ line_item: lineItem, year, quantity }),
    })
    return unwrap<CostCell>(res)
  },

  /** Dry-run returns the modal counts; a real call soft-deletes the subtree. */
  async deactivateCategory(id: number, opts: { dryRun?: boolean } = {}): Promise<SubtreeCounts> {
    const qs = opts.dryRun ? '?dry_run=1' : ''
    const res = await fetch(`${API_BASE_URL}/api/cost-categories/${id}/deactivate-subtree/${qs}`, {
      method: 'POST', headers: authHeaders(),
    })
    return unwrap<SubtreeCounts>(res)
  },

  async searchProductServiceCodes(search: string): Promise<ProductServiceCode[]> {
    const res = await fetch(
      `${API_BASE_URL}/api/product-service-codes/?search=${encodeURIComponent(search)}`,
      { headers: authHeaders(false) },
    )
    const data = await unwrap<any>(res)
    return Array.isArray(data) ? data : (data.results ?? [])
  },

  /** Stream the xlsx export and trigger a browser download. */
  async exportXlsx(id: number, filename = `cost-estimate-${id}.xlsx`): Promise<void> {
    await downloadFile(`${API_BASE_URL}/api/cost-models/${id}/export-xlsx/`, filename)
  },
}

/** Fetch an authenticated file and trigger a browser download. API error bodies
 *  (e.g. a 409 with a `detail`) surface as the thrown message. */
export async function downloadFile(url: string, filename: string): Promise<void> {
  const res = await fetch(url, { headers: authHeaders(false) })
  if (!res.ok) {
    await unwrap(res)  // throws with the API's message
  }
  const blob = await res.blob()
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.click()
  URL.revokeObjectURL(href)
}
